import React, { useState, useEffect } from 'react';

export default function RecruitmentManagement() {
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [showApplications, setShowApplications] = useState(false);
  
  const [formData, setFormData] = useState({
    category: '', 
    title: '', 
    overview: '', 
    what_you_do: '',
    requirements: '', 
    what_we_offer: '', 
    location: 'Remote',
    employment_type: 'Full-time', 
    salary_range: '', 
    deadline: ''
  });

  useEffect(() => {
    fetchAdminSummary();
  }, []);

  // Use environment variable for API base - set in .env.production
  const API_BASE = process.env.REACT_APP_API_BASE ? `${process.env.REACT_APP_API_BASE}/join` : 'http://localhost:3000/api/join';

  const fetchAdminSummary = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${API_BASE}/jobs/admin-summary`);
      if (!res.ok) throw new Error('Failed to fetch jobs');
      const data = await res.json();
      setJobs(data);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching jobs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      
      const res = await fetch(`${API_BASE}/jobs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to create position');
      }
      
      alert('✅ Position opened successfully!');
      fetchAdminSummary();
      setFormData({ 
        category: '', 
        title: '', 
        overview: '', 
        what_you_do: '', 
        requirements: '', 
        what_we_offer: '', 
        location: 'Remote', 
        employment_type: 'Full-time', 
        salary_range: '', 
        deadline: '' 
      });
    } catch (err) {
      setError(err.message);
      alert('❌ Error: ' + err.message);
      console.error('Error creating position:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewApplications = async (job) => {
    setSelectedJob(job);
    setLoading(true);
    try {
      // Fetch applications for this job
      const res = await fetch(`${API_BASE}/jobs/${job.id}/applications`);
      if (!res.ok) throw new Error('Failed to fetch applications');
      const data = await res.json();
      setApplications(data);
    } catch (err) {
      console.error('Error fetching applications:', err);
      setApplications([]);
    } finally {
      setLoading(false);
      setShowApplications(true);
    }
  };

  const handleCloseApplications = () => {
    setShowApplications(false);
    setSelectedJob(null);
    setApplications([]);
  };

  const handleUpdateApplicationStatus = async (applicationId, newStatus) => {
    try {
      const res = await fetch(`${API_BASE}/jobs/applications/${applicationId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (!res.ok) throw new Error('Failed to update application status');
      
      // Update local state
      setApplications(applications.map(app => 
        app.id === applicationId ? { ...app, status: newStatus } : app
      ));
      alert('✅ Application status updated!');
    } catch (err) {
      alert('❌ Error: ' + err.message);
      console.error('Error updating application status:', err);
    }
  };

  return (
    <div className="p-8 bg-gray-100 min-h-screen text-gray-800">
      <h1 className="text-3xl font-bold mb-2">Recruitment Control Center</h1>
      <p className="text-gray-600 mb-6">Backend: {API_BASE}</p>
      
      {error && (
        <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
          ⚠️ {error}
        </div>
      )}
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Create Position Form */}
        <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-md space-y-4">
          <h2 className="text-xl font-semibold mb-4">📋 Open New Position</h2>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium">Category</label>
              <input 
                type="text" 
                name="category" 
                placeholder="e.g. Engineering" 
                value={formData.category} 
                onChange={handleChange} 
                className="w-full border p-2 rounded" 
                required 
              />
            </div>
            <div>
              <label className="block text-sm font-medium">Post Title</label>
              <input 
                type="text" 
                name="title" 
                placeholder="e.g. Senior Developer" 
                value={formData.title} 
                onChange={handleChange} 
                className="w-full border p-2 rounded" 
                required 
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium">Overview</label>
            <textarea 
              name="overview" 
              value={formData.overview} 
              onChange={handleChange} 
              className="w-full border p-2 rounded h-16" 
              placeholder="Job description overview..."
              required 
            />
          </div>

          <div>
            <label className="block text-sm font-medium">What You'll Do</label>
            <textarea 
              name="what_you_do" 
              value={formData.what_you_do} 
              onChange={handleChange} 
              className="w-full border p-2 rounded h-16" 
              placeholder="Describe daily responsibilities..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium">Requirements</label>
            <textarea 
              name="requirements" 
              value={formData.requirements} 
              onChange={handleChange} 
              className="w-full border p-2 rounded h-16" 
              placeholder="Required skills and experience..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium">What We Offer</label>
            <textarea 
              name="what_we_offer" 
              value={formData.what_we_offer} 
              onChange={handleChange} 
              className="w-full border p-2 rounded h-16" 
              placeholder="Benefits and perks..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium">Location</label>
              <input 
                type="text" 
                name="location" 
                value={formData.location} 
                onChange={handleChange} 
                className="w-full border p-2 rounded"
                placeholder="e.g. Remote, New York"
              />
            </div>
            <div>
              <label className="block text-sm font-medium">Employment Type</label>
              <select 
                name="employment_type" 
                value={formData.employment_type} 
                onChange={handleChange} 
                className="w-full border p-2 rounded"
              >
                <option value="Full-time">Full-Time</option>
                <option value="Part-time">Part-Time</option>
                <option value="Contract">Contract</option>
                <option value="Internship">Internship</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium">Salary Range</label>
              <input 
                type="text" 
                name="salary_range" 
                placeholder="e.g. $80k - $120k" 
                value={formData.salary_range} 
                onChange={handleChange} 
                className="w-full border p-2 rounded" 
              />
            </div>
            <div>
              <label className="block text-sm font-medium">Application Deadline</label>
              <input 
                type="date" 
                name="deadline" 
                value={formData.deadline} 
                onChange={handleChange} 
                className="w-full border p-2 rounded" 
                required 
              />
            </div>
          </div>

          <button 
            type="submit" 
            disabled={loading}
            className="w-full bg-blue-600 text-white py-3 rounded font-semibold hover:bg-blue-700 disabled:bg-gray-400 transition"
          >
            {loading ? '⏳ Publishing...' : '🚀 Publish Position'}
          </button>
        </form>

        {/* View Openings and Applicants Counts */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold mb-4">📊 Active Open Positions</h2>
          
          {loading && !jobs.length && (
            <p className="text-gray-500">⏳ Loading positions...</p>
          )}
          
          {jobs.length === 0 && !loading ? (
            <p className="text-gray-500">📭 No open positions yet</p>
          ) : (
            <div className="space-y-3 max-h-[600px] overflow-y-auto">
              {jobs.map((job) => (
                <div 
                  key={job.id} 
                  className="p-4 border rounded-lg bg-gradient-to-r from-gray-50 to-gray-100 hover:shadow-md transition"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-bold text-lg">{job.title}</h3>
                      <p className="text-sm text-gray-600">{job.category} • {job.location}</p>
                      <div className="flex gap-2 mt-2">
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                          {job.employment_type || 'Full-time'}
                        </span>
                        {job.salary_range && (
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                            {job.salary_range}
                          </span>
                        )}
                      </div>
                      {job.deadline && (
                        <p className="text-xs text-red-500 font-medium mt-2">
                          📅 Deadline: {new Date(job.deadline).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-center bg-blue-100 text-blue-800 px-3 py-2 rounded-md mb-2">
                        <span className="block text-2xl font-bold">{job.total_applications || 0}</span>
                        <span className="text-xs font-semibold">Applications</span>
                      </div>
                      {(job.total_applications || 0) > 0 && (
                        <button
                          onClick={() => handleViewApplications(job)}
                          className="text-xs bg-green-600 text-white px-2 py-1 rounded hover:bg-green-700 transition"
                        >
                          👥 View
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Applications Modal */}
      {showApplications && selectedJob && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-4xl w-full max-h-[80vh] overflow-auto p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">
                Applications for: {selectedJob.title}
              </h2>
              <button
                onClick={handleCloseApplications}
                className="text-2xl text-gray-600 hover:text-gray-800"
              >
                ✕
              </button>
            </div>
            
            {applications.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-600 text-lg">📭 No applications yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {applications.map((app) => (
                  <div key={app.id} className="border rounded-lg p-4 bg-gray-50 hover:bg-gray-100 transition">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                      <div>
                        <p className="text-sm font-bold text-gray-600">Name</p>
                        <p className="text-lg font-semibold">{app.full_name}</p>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-600">Email</p>
                        <p className="text-sm text-blue-600">
                          <a href={`mailto:${app.email}`} className="hover:underline">{app.email}</a>
                        </p>
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-600">Phone</p>
                        <p className="text-sm">{app.phone_number}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
                      {app.linkedin_url && (
                        <div>
                          <p className="text-sm font-bold text-gray-600">LinkedIn</p>
                          <a href={app.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline break-all">
                            {app.linkedin_url}
                          </a>
                        </div>
                      )}
                      {app.portfolio_url && (
                        <div>
                          <p className="text-sm font-bold text-gray-600">Portfolio</p>
                          <a href={app.portfolio_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline break-all">
                            {app.portfolio_url}
                          </a>
                        </div>
                      )}
                    </div>

                    {app.resume_path && (
                      <div className="mb-3">
                        <p className="text-sm font-bold text-gray-600">Resume</p>
                        <a href={`${API_BASE.replace('/join', '')}/uploads/resumes/${app.resume_path.split('/').pop()}`} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline">
                          📄 Download Resume
                        </a>
                      </div>
                    )}

                    {app.cover_letter && (
                      <div className="mb-3 bg-white p-2 rounded border border-gray-200">
                        <p className="text-sm font-bold text-gray-600 mb-1">Cover Letter</p>
                        <p className="text-sm text-gray-700">{app.cover_letter}</p>
                      </div>
                    )}

                    <div className="flex items-center gap-3 pt-3 border-t">
                      <span className="text-sm font-bold text-gray-600">Status:</span>
                      <select 
                        value={app.status}
                        onChange={(e) => handleUpdateApplicationStatus(app.id, e.target.value)}
                        className={`px-3 py-1 rounded text-sm font-semibold cursor-pointer ${
                          app.status === 'received' ? 'bg-blue-100 text-blue-800' :
                          app.status === 'reviewed' ? 'bg-purple-100 text-purple-800' :
                          app.status === 'shortlisted' ? 'bg-green-100 text-green-800' :
                          app.status === 'rejected' ? 'bg-red-100 text-red-800' :
                          'bg-amber-100 text-amber-800'
                        }`}
                      >
                        <option value="received">📧 Received</option>
                        <option value="reviewed">👀 Reviewed</option>
                        <option value="shortlisted">✅ Shortlisted</option>
                        <option value="rejected">❌ Rejected</option>
                        <option value="accepted">🎉 Accepted</option>
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}